import { useMediaQuery } from 'react-responsive';

import { Alert } from 'honeydew/Alert';
import Button from 'honeydew/Button';
import { Drawer } from 'honeydew/Drawer';
import { LoadingIcon } from 'honeydew/Loader';
import Modal from 'honeydew/Modal';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { trackEvent, updateProfile } from 'utils/analyticsUtils';
import { formatDateString, localizeTimeZone } from 'utils/dateUtils';

/**
 * Modal component for confirming EBT card deletion.
 * Renders as a drawer on mobile and modal on desktop.
 *
 * @param {Function} deleteCard - Function to call when delete is confirmed
 * @param {Object} deleteError - Error object from delete operation
 * @param {number|null} ebtAmount - EBT charge amount to display in refund message
 * @param {boolean} isDeleting - Whether the delete operation is in progress
 * @param {boolean} isEBTChargeAmountLoading - Whether EBT charge amount is loading
 * @param {string|null} marketCloseDate - Market close date string to display in refund message
 * @param {Function} onClose - Function to call when modal is closed
 * @param {boolean} open - Whether the modal is visible
 */
export default function EBTDeleteCardModal({
    deleteCard,
    deleteError,
    ebtAmount,
    isDeleting,
    isEBTChargeAmountLoading,
    marketCloseDate,
    onClose,
    open,
}) {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const handleDelete = async () => {
        await deleteCard();

        trackEvent('SNAP card remove', {
            location: 'account',
        });
        updateProfile({ SNAP_enabled: false });

        onClose();
    };

    const renderContent = () => {
        if (isEBTChargeAmountLoading) {
            return (
                <div className="flex h-[300px] items-center justify-center">
                    <LoadingIcon />
                </div>
            );
        }

        return (
            <>
                <h3 className="text-heading-xs">Remove EBT Card?</h3>
                {ebtAmount > 0 && (
                    <Alert variant="warning">
                        <span className="block">
                            Your applied payment of ${Number(ebtAmount).toFixed(2)} will be refunded if you remove your
                            card prior to your window close{' '}
                            {marketCloseDate
                                ? `at ${formatDateString(marketCloseDate, true)} ${localizeTimeZone(marketCloseDate)}`
                                : ''}
                        </span>
                        <span className="mt-10 block">
                            If your order meets your minimum when your shopping window closes, your default payment
                            method will be charged for the full value of your order.
                        </span>
                    </Alert>
                )}
                <p>Once deleted, you&apos;ll need to re-add it to use EBT for future purchases.</p>
                <p>Note: Only one EBT card can be linked to your account at a time.</p>

                {deleteError && <Alert variant="error">There was an error deleting your card. Please try again.</Alert>}
                <Button onClick={handleDelete} disabled={isDeleting}>
                    {isDeleting ? (
                        <span className="flex items-center gap-10">
                            <LoadingIcon /> Deleting...
                        </span>
                    ) : (
                        'Delete EBT Card'
                    )}
                </Button>
                <p className="text-center">
                    <button onClick={onClose} className="link text-body-sm" type="button">
                        Cancel
                    </button>
                </p>
            </>
        );
    };

    if (mobile) {
        return (
            <Drawer anchor="bottom" open={open} toggleDrawer={onClose}>
                <div className="space-y-15 p-20">{renderContent()}</div>
            </Drawer>
        );
    }

    return (
        <Modal open={open} onClose={onClose}>
            <div className="mx-auto max-w-md space-y-20">{renderContent()}</div>
        </Modal>
    );
}
